import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Store from '@apply/store';
import { Header } from '../common_v2/components/Header';
import { useAuthentication } from '../Login/hooks/useAuthentication';
import { alertModal, loginAlertModal, isMobileScreen } from '@apply/ui';
import { Button, ButtonTheme, CheckButton, CheckButtonFontStyle, SignaturePopup } from '@apply/components';
import './scss/_userAgreementChanged.scss';
import { Footer } from '../common_v2/components/Footer';
import parse from 'html-react-parser';
import { LoginApi } from '@apply/api/src/api/loginApi.ts';
import { applicationInsights } from '../ApplicationInsights';
import { logApplicationInsightsError } from '../common/functions/applicationInsightFunctions';

const getElectronicRecordsHtml = async () => Store.CrossStore.getElectronicRecordsHtml();
const saveEsignDetails = async (userId: string, userAgreementTypeId: number, fontId: number, ipAddress: string) =>
    Store.CrossStore.signUserAgreement(userId, userAgreementTypeId, fontId, ipAddress);

export const ResignAgreement = () => {
    const FONT_ID = '1';
    const mobileScreen = isMobileScreen();
    const { isRegisteredUser, authentication, isAuthenticated, logout } = useAuthentication();

    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string>('');
    const [ipAddress, setIpAddress] = useState('');
    const [esignHtml, setEsignHtml] = useState('');
    const [agreementOpen, setAgreementOpen] = useState(false);
    const [eSignFontId, setESignFontId] = useState('');
    const [agreeToEsignAgreement, setAgreeToEsignAgreement] = useState(false);

    const navigate = useNavigate();
    const thisLocation = useLocation();
    const loginApi = new LoginApi(applicationInsights);

    const agreementLabel =
        'Consent to Use Electronic Records and Disclosures. By checking this check box and clicking "Save", I acknowledge that I have read and agree on behalf of the Business Entities to the Consent to Use Electronic Records and Disclosures.';

    // Redirect if not authenticated
    useEffect(() => {
        if (authentication && (!isAuthenticated || !isRegisteredUser)) {
            window.location.href = '/login';
        }
    }, [authentication, isAuthenticated, isRegisteredUser]);

    useEffect(() => {
        const fetchIpAddress = async () => {
            const response = await loginApi.getIpAddress();

            if (response.statusCode !== 200) {
                logApplicationInsightsError(
                    new Error('Failed to retrieve IP address! External service failed to return IP address.')
                );
                return;
            }

            setIpAddress(response.result);
        };

        fetchIpAddress();
    }, []);

    // Handle load errors
    useEffect(() => {
        if (!loadError) return;

        const unauthorized = String(loadError).includes('401 - User not Authorized.');

        if (unauthorized) {
            loginAlertModal({
                onConfirm: () => {
                    setLoadError('');
                    logout();
                    navigate(`/login?redirectTo=${thisLocation.pathname}`);
                },
            });
        } else {
            alertModal({
                message: 'An error has occurred while communicating with our server. Please try your operation again.',
                onConfirm: () => setLoadError(''),
            });
        }
    }, [loadError, logout, navigate, thisLocation.pathname]);

    const onConfirmAgreement = () => {
        setAgreementOpen(false);
        setESignFontId(FONT_ID);
        setAgreeToEsignAgreement(true);
    };

    const onRejectAgreement = () => {
        setAgreementOpen(false);
        setAgreeToEsignAgreement(false);
    };

    const onClickESignAgreement = async () => {
        const html = await getElectronicRecordsHtml();
        setEsignHtml(html);
        setAgreementOpen(true);
    };

    const onSave = async () => {
        try {
            setLoading(true);
            const user = authentication?.user;

            await saveEsignDetails(user.userId, 0, Number(eSignFontId), ipAddress);

            window.location.href = '/home';
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className='app-container'>
                <Header />
                <div className='user-agreement-changed'>
                    <h2>Our User Agreement Has Changed</h2>
                    <p>
                        <b>In order to continue to use this website you must agree to the new terms.</b>
                    </p>
                    <p>Please click the consent checkbox below and sign the agreement.</p>

                    <div className='user-agreement-changed-acceptance'>
                        <CheckButton
                            id='agreeToEsignAgreement'
                            fontStyle={CheckButtonFontStyle.Large}
                            label={agreementLabel}
                            selected={agreeToEsignAgreement}
                            onToggle={onClickESignAgreement}
                            width={mobileScreen ? '100%' : '600px'}
                            disabled={!isAuthenticated || loading}
                            hideCheckbox={false}
                            anchorCheckboxTop={true}
                        />
                    </div>

                    <div className='user-agreement-changed-actions'>
                        <Button
                            id='saveEsignAgreement'
                            theme={ButtonTheme.Primary}
                            width='200px'
                            handleClick={onSave}
                            disabled={!agreeToEsignAgreement || loading}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </div>
            <Footer />

            <SignaturePopup
                id='acceptUpdatedAgreementPopup'
                onSubmitAgreement={onConfirmAgreement}
                onDeclineAgreement={onRejectAgreement}
                isOpen={agreementOpen}
                firstName={authentication?.user.firstName ?? ''}
                lastName={authentication?.user.lastName ?? ''}
            >
                {parse(esignHtml ?? '')}
            </SignaturePopup>
        </>
    );
};
